import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { iframeCacheInvalidationRules } from '../config';

export function useIframeCacheInvalidation(iframePath: string, queryClient: QueryClient): void {
  const previousPathRef = useRef<string>('');
  const wasOnTriggerPathRef = useRef(false);

  // Track when user is on a path that triggers invalidation
  useEffect(() => {
    const onTriggerPath = iframeCacheInvalidationRules.some(rule =>
      iframePath.includes(rule.pathSegment)
    );
    if (onTriggerPath) {
      wasOnTriggerPathRef.current = true;
    }
  }, [iframePath]);

  // Invalidate cache when navigating away from a trigger path
  useEffect(() => {
    const previousPath = previousPathRef.current;
    previousPathRef.current = iframePath;

    const ruleForPreviousPath = iframeCacheInvalidationRules.find(rule =>
      previousPath.includes(rule.pathSegment)
    );
    const leftTriggerPath =
      ruleForPreviousPath && !iframePath.includes(ruleForPreviousPath.pathSegment);

    if (leftTriggerPath) {
      wasOnTriggerPathRef.current = false;
      const queryKeys = ruleForPreviousPath.queryKeys;
      queryClient.invalidateQueries({
        predicate: query =>
          typeof query.queryKey[0] === 'string' && queryKeys.includes(query.queryKey[0]),
      });
    }
  }, [iframePath, queryClient]);

  // Invalidate when user switches back to this tab after visiting a trigger path (e.g. edited in legacy in another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !wasOnTriggerPathRef.current) {
        return;
      }
      wasOnTriggerPathRef.current = false;
      const allQueryKeys = iframeCacheInvalidationRules.flatMap(r => r.queryKeys);
      queryClient.invalidateQueries({
        predicate: query =>
          typeof query.queryKey[0] === 'string' && allQueryKeys.includes(query.queryKey[0]),
      });
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [queryClient]);
}
